/**
 * Central route registry for the app.
 *
 * Each route defines:
 * - `path`  — URL pattern (static or with ":id")
 * - `page`  — zero-props React page component (lazy-loaded)
 *
 * Why store them together:
 * - Keeps navigation and routing in sync (single source of truth)
 * - Pages declare their own URL at the definition point
 * - No hard-coded paths scattered across components or hooks
 *
 * About `build(id)`:
 * - Auto-generated for dynamic routes ("/x/:id")
 * - Used to create real URLs from route templates
 *   Example: ROUTES.templates.view.build("123") → "/t/123"
 *
 * Usage:
 * - Router consumes `path`/`page` to generate <Route> elements
 * - Components use `.build()` when navigating programmatically
 */

import React from 'react'
export type Route = {
    path: string
    build: (id: string) => string
    page: React.LazyExoticComponent<React.ComponentType>
}

export type Routes = {
    [group: string]: {
        [name: string]: Route
    }
}

/**
 * Replaces `:id` in a path with a given value.
 *
 * @param path The path template (e.g., "/users/:id/posts").
 * @param id The value to substitute for `:id`.
 * @returns The path with `:id` replaced.
 */
export function buildPath(path: string, id: string): string {
    return path.replace(/\/:id(\/|$)/, `/${id}$1`)
}

function route(r: Omit<Route, 'build'>): Route {
    return {
        build: (id: string) => buildPath(r.path, id),
        ...r,
    }
}

export const ROUTES: Routes = {
    encounters: {
        list: route({
            path: '/e',
            page: React.lazy(() => import('./components/Encounter/EncounterList/EncountersList')),
        }),
        new: route({
            path: '/e/new',
            page: React.lazy(() =>
                import('./components/Encounter/Encounter').then((m) => ({
                    default: m.NewEncounterPage,
                }))
            ),
        }),
        view: route({
            path: '/e/:id',
            page: React.lazy(() =>
                import('./components/Encounter/Encounter').then((m) => ({
                    default: m.EncounterPage,
                }))
            ),
        }),
    },

    templates: {
        list: route({
            path: '/t',
            page: React.lazy(() => import('./components/Template/TemplateList')),
        }),
        new: route({
            path: '/t/new',
            page: React.lazy(() =>
                import('./components/Template/TemplateNew').then((m) => ({
                    default: m.TemplateEditPage,
                }))
            ),
        }),
        view: route({
            path: '/t/:id',
            page: React.lazy(() =>
                import('./components/Template/TemplateView').then((m) => ({
                    default: m.TemplateViewPage,
                }))
            ),
        }),
        edit: route({
            path: '/t/:id/edit',
            page: React.lazy(() =>
                import('./components/Template/TemplateNew').then((m) => ({
                    default: m.TemplateEditPage,
                }))
            ),
        }),
    },

    templateLibrary: {
        list: route({
            path: '/tl',
            page: React.lazy(() => import('./components/Template/TemplateLibrary/TemplateLibrary')),
        }),
        view: route({
            path: '/tl/:id',
            page: React.lazy(() =>
                import('./components/Template/TemplateView').then((m) => ({
                    default: m.TemplateLibraryViewPage,
                }))
            ),
        }),
        externalView: route({
            path: '/tl/external/:id',
            page: React.lazy(() =>
                import('./components/Template/TemplateView').then((m) => ({
                    default: m.ExternalTemplateViewPage,
                }))
            ),
        }),
    },

    admin: {
        users: route({
            path: '/admin/users',
            page: React.lazy(() => import('./components/Admin/UserList')),
        }),
    },

    groups: {
        list: route({
            path: '/g',
            page: React.lazy(() => import('./components/Groups/GroupsPage')),
        }),
        view: route({
            path: '/g/:id',
            page: React.lazy(() => import('./components/Groups/GroupsPage')),
        }),
    },

    settings: {
        main: route({
            path: '/settings',
            page: React.lazy(() => import('./components/Settings/SettingsPage')),
        }),
    },

    integrations: {
        list: route({
            path: '/integrations',
            page: React.lazy(() => import('./components/Integrations/IntegrationsList')),
        }),
        callback: route({
            path: '/adapters/callback',
            page: React.lazy(() => import('./components/Integrations/AdaptersCallback')),
        }),
    },

    dev: {
        panel: route({
            path: '/dev',
            page: React.lazy(() => import('./pages/DevPanel')),
        }),
    },
}
